/**
 * Dashboard (route `/`).
 *
 * The home page for every signed-in user. Three sections:
 *   1. Stat-tile strip — counts of all-projects, active, overdue, pending approval
 *   2. Recent overdue (everyone) — first 5 projects with overdue indicators
 *   3. Pending approvals (Admin only) — submissions awaiting review
 *
 * Data: a single `/api/visibility/projects` call drives 1 + 2. The Inbox call
 * (Admin only) drives 3.
 */
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, FileWarning } from 'lucide-react';
import { formatCurrency, formatRelative } from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { ProjectProgressBar } from '@/components/project-progress-bar';
import { Sparkline } from '@/components/sparkline';
import { Spinner } from '@/components/ui/spinner';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCurrentUser } from '@/lib/auth';
import { useInbox, useProjectsList, useReceivables, useThroughput } from '@/lib/visibility-queries';
import { usePageTitle } from '@/lib/use-page-title';

export function DashboardPage() {
  usePageTitle('Dashboard');
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';

  // Fetch all open projects so we can derive client-side counts. With small
  // dataset sizes (≤ a few hundred projects) this is cheaper than four
  // separate filtered requests, and the existing endpoint already returns
  // each row's derived state.
  const projects = useProjectsList({ pageSize: 200, excludeClosed: true });
  const inbox = useInbox({ enabled: isAdmin });
  // E21: BG exposure tile sits next to the lifecycle counts.
  const receivables = useReceivables();
  // E25: monthly throughput sparkline.
  const throughput = useThroughput();

  if (projects.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  // Patch 5 — surface API failures rather than silently rendering empty
  // arrays. The dashboard's other queries (receivables, throughput, inbox)
  // each render their own placeholders inline; this gate only covers the
  // primary projects query which everything else builds on.
  if (projects.isError || !projects.data) {
    return (
      <Card>
        <ErrorPanel
          title="Could not load dashboard"
          body="The server didn’t return projects."
          onRetry={() => projects.refetch()}
        />
      </Card>
    );
  }

  const all = projects.data?.items ?? [];
  const active = all.filter((p) => p.lifecycleState === 'ACTIVE');
  const overdue = all.filter(
    (p) => p.isOverdue || p.overdueBatchCount > 0 || p.unresolvedInspectionCount > 0,
  );
  const pendingApproval = all.filter((p) => p.hasPendingPartsApproval);
  const bgExposurePaisa = receivables.data?.aggregate.activeBgExposurePaisa ?? 0;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome back, ${me.data?.user.displayName ?? ''}.`}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile label="Open projects" value={all.length} />
        <StatTile label="Active" value={active.length} tone="neutral" />
        <StatTile
          label="Overdue"
          value={overdue.length}
          tone={overdue.length > 0 ? 'danger' : 'neutral'}
          hint={overdue.length > 0 ? 'Needs attention' : 'All on track'}
        />
        <StatTile
          label="Pending approval"
          value={pendingApproval.length}
          tone={pendingApproval.length > 0 ? 'warning' : 'neutral'}
          hint={isAdmin ? 'Awaiting your review' : 'Submitted to Admin'}
        />
        {/* E21: total capital locked in BGs sitting with the client. ACTIVE +
         * ACCEPTED only — RELEASED/ENCASHED/REPLACED no longer count as
         * exposure. Loading state shows '—' rather than blocking the tile. */}
        <StatTile
          label="BG exposure"
          value={
            receivables.isLoading
              ? '—'
              : bgExposurePaisa > 0
                ? formatCurrency(bgExposurePaisa, { compact: true })
                : '—'
          }
          hint={
            bgExposurePaisa > 0 ? 'Capital with the client' : 'No live BGs'
          }
        />
      </div>

      {/* E25: throughput card — projects closed per calendar month, rolling
       * 6-month window. Sits between the at-a-glance tiles and the two-column
       * detail body so trend reads at the same vantage as the counts above. */}
      <div className="mb-6">
        <ThroughputCard data={throughput.data} isLoading={throughput.isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card
          title="Overdue & blocked"
          actions={
            <Link
              to="/projects"
              search={{ overdueOnly: true }}
              className="flex items-center gap-1 text-xs text-secondary hover:text-primary"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          }
          bodyClassName="p-0"
        >
          {overdue.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6 text-status-success" />}
              title="Nothing overdue"
              body="No projects with overdue batches or unresolved inspections."
            />
          ) : (
            <ul className="divide-y divide-border-default">
              {overdue.slice(0, 6).map((p) => (
                <li key={p.projectId}>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: p.projectId }}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <ProjectNumber number={p.projectNumber} size="sm" />
                        <span className="truncate font-medium text-primary">{p.projectTitle}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-secondary">
                        {p.overdueBatchCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-status-danger">
                            <AlertTriangle className="h-3 w-3" />
                            {p.overdueBatchCount} overdue{' '}
                            {p.overdueBatchCount === 1 ? 'batch' : 'batches'}
                          </span>
                        ) : null}
                        {p.unresolvedInspectionCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-status-warning">
                            <FileWarning className="h-3 w-3" />
                            {p.unresolvedInspectionCount} unresolved
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <StatusBadge kind="lifecycle" value={p.lifecycleState} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {isAdmin ? (
          <Card
            title="Pending parts approvals"
            actions={
              <Link
                to="/inbox"
                className="flex items-center gap-1 text-xs text-secondary hover:text-primary"
              >
                Open inbox <ArrowRight className="h-3 w-3" />
              </Link>
            }
            bodyClassName="p-0"
          >
            {inbox.isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner className="h-5 w-5 text-secondary" />
              </div>
            ) : (inbox.data?.pendingPartsApprovals.length ?? 0) === 0 ? (
              <EmptyState
                icon={<CheckCircle2 className="h-6 w-6 text-status-success" />}
                title="Inbox zero"
                body="No parts approvals waiting for your review."
              />
            ) : (
              <ul className="divide-y divide-border-default">
                {(inbox.data?.pendingPartsApprovals ?? []).slice(0, 6).map((sub) => (
                  <li key={sub.projectId}>
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: sub.projectId }}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <ProjectNumber number={sub.projectNumber} size="sm" />
                          <span className="truncate font-medium text-primary">
                            {sub.projectTitle}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-secondary">
                          <Clock className="h-3 w-3" />
                          Submitted by {sub.submittedByDisplayName} ·{' '}
                          {formatRelative(sub.submittedAt)}
                        </div>
                      </div>
                      <StatusBadge kind="partsWorkflow" value="PENDING_APPROVAL" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : (
          <Card title="Your projects" bodyClassName="p-0">
            {active.length === 0 ? (
              <EmptyState
                title="No active projects"
                body="Projects you're working on will appear here once parts are finalised."
              />
            ) : (
              <ul className="divide-y divide-border-default">
                {active.slice(0, 6).map((p) => (
                  <li key={p.projectId}>
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.projectId }}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <ProjectNumber number={p.projectNumber} size="sm" />
                          <span className="truncate font-medium text-primary">
                            {p.projectTitle}
                          </span>
                        </div>
                        {/* E20: progress bar gives a manager an instant
                         * read of where this project is relative to peers,
                         * without having to drill in. */}
                        <div className="mt-1">
                          <ProjectProgressBar
                            buckets={p.partsProgress}
                            variant="compact"
                          />
                        </div>
                        <div className="mt-1 text-xs text-secondary">
                          {p.partCount} parts · {p.activeBatchCount} active batches
                        </div>
                      </div>
                      <StatusBadge kind="lifecycle" value={p.lifecycleState} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// E25: Throughput card
// ---------------------------------------------------------------------------

/**
 * Friendly month label from a 'YYYY-MM' identifier.
 *
 * Output: "May 26" (short month + 2-digit year) — compact enough for the
 * 6-column legend strip below the sparkline, while still unambiguous.
 */
function shortMonthLabel(yearMonth: string): string {
  const [y, m] = yearMonth.split('-');
  const monthIdx = Number(m) - 1;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[monthIdx] ?? '?'} ${(y ?? '').slice(2)}`;
}

function ThroughputCard({
  data,
  isLoading,
}: {
  data: ReturnType<typeof useThroughput>['data'];
  isLoading: boolean;
}) {
  if (isLoading || !data) {
    return (
      <Card title="Throughput">
        <div className="flex items-center justify-center py-6">
          <Spinner className="h-4 w-4 text-secondary" />
        </div>
      </Card>
    );
  }

  const values = data.months.map((m) => m.closedCount);
  const total = values.reduce((s, v) => s + v, 0);
  const allZero = total === 0;
  const deltaVsAvg = data.currentMonthClosed - data.trailingAverage;
  // Round trailing average to 1 dp for display — anything more reads as
  // false precision.
  const avgDisplay = data.trailingAverage.toFixed(1);

  let trendCopy: { text: string; tone: 'success' | 'warning' | 'neutral' };
  if (allZero) {
    trendCopy = { text: 'No closures in the last 6 months.', tone: 'neutral' };
  } else if (data.currentMonthClosed === 0 && data.trailingAverage > 0) {
    trendCopy = { text: `0 this month — average ${avgDisplay}/mo.`, tone: 'warning' };
  } else if (deltaVsAvg >= 1) {
    trendCopy = {
      text: `+${deltaVsAvg.toFixed(1)} vs 5-month average of ${avgDisplay}/mo.`,
      tone: 'success',
    };
  } else if (deltaVsAvg <= -1) {
    trendCopy = {
      text: `${deltaVsAvg.toFixed(1)} vs 5-month average of ${avgDisplay}/mo.`,
      tone: 'warning',
    };
  } else {
    trendCopy = {
      text: `In line with 5-month average of ${avgDisplay}/mo.`,
      tone: 'neutral',
    };
  }

  const toneClass =
    trendCopy.tone === 'success'
      ? 'text-success'
      : trendCopy.tone === 'warning'
        ? 'text-warning-strong'
        : 'text-secondary';

  return (
    <Card title="Throughput">
      <div className="flex flex-wrap items-center gap-6">
        {/* Headline number + delta copy */}
        <div className="min-w-[140px]">
          <div className="text-xs font-medium uppercase tracking-wide text-tertiary">
            Closed this month
          </div>
          <div className="mt-0.5 text-3xl font-semibold tabular-nums text-primary">
            {data.currentMonthClosed}
          </div>
          <div className={`mt-1 text-xs ${toneClass}`}>{trendCopy.text}</div>
        </div>

        {/* Sparkline + month axis */}
        <div className="flex flex-1 flex-col gap-1 min-w-[260px]">
          <Sparkline values={values} width={320} height={48} className="text-secondary" />
          <div className="flex justify-between text-[10px] tabular-nums text-tertiary">
            {data.months.map((m, i) => (
              <span
                key={m.yearMonth}
                className={i === data.months.length - 1 ? 'font-medium text-secondary' : ''}
              >
                {shortMonthLabel(m.yearMonth)}
              </span>
            ))}
          </div>
        </div>

        {/* 6-month total */}
        <div className="text-right">
          <div className="text-xs font-medium uppercase tracking-wide text-tertiary">
            6-month total
          </div>
          <div className="mt-0.5 text-3xl font-semibold tabular-nums text-primary">{total}</div>
          <div className="mt-1 text-xs text-secondary">projects closed</div>
        </div>
      </div>
    </Card>
  );
}
